import {
  memo, useEffect, useReducer, useRef, useState,
} from 'react';
import type { ReactNode } from 'react';
import useUnmountPromise from 'react-use/lib/useUnmountPromise';
import cx from 'classnames';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import ButtonToolbar from 'react-bootstrap/ButtonToolbar';
import Dropdown from 'react-bootstrap/Dropdown';

import Icon from '../components/Icon';
import {
  DataTable as DataTableComponent,
  CustomDataTable as CustomDataTableComponent,
  DataTableV2,
  isDataTableV2Enabled,
} from '../components/datatable';
import { QuickSearch as QuickSearchInput } from '../components/search';
import { Button } from '../components/buttons';
import { useComponentDidUpdate } from '../hooks';
import { DocumentTitle } from '../components/title';
import omit from 'lodash/omit';

const storage = localStorage;
const stateStorageKey = 'state';
const filterStorageKey = 'filters';
const hiddenColumnsStorageKey = 'hiddenColumns';
const paginationSizeStorageKey = 'paginationSizeKey';
const columnWidthsStorageKey = 'columnWidths';

// Стабильные ссылки для дефолтов-объектов/массивов (чтобы не создавать новые на каждом рендере).
// Важно: statusRefValues и initialRequest участвуют в зависимостях/инициализации хуков.
const EMPTY_DATA: any[] = [];
const EMPTY_FILTER_VALUES: Record<string, any> = {};
const EMPTY_HIDDEN_COLUMN_NAMES: string[] = [];
const EMPTY_STATUS_REF_VALUES: any[] = [];
const DEFAULT_INITIAL_REQUEST = {
  offset: 0,
  size: 10,
  sortField: 'createdAt',
  sortOrder: 'DESC',
};

interface DataTablePageToolbarProps {
  isHighlighted?: boolean;
  children?: ReactNode;
  onSaveColumns: () => void;
  onSaveFilters: () => void;
  onSaveAll: () => void;
  onReset: () => void;
  dontStorePaginationInStorage?: boolean;
}

const DataTableToolbar = (props: DataTablePageToolbarProps) => {
  const {
    isHighlighted,
    children,
    onSaveColumns,
    onSaveFilters,
    onSaveAll,
    onReset,
    dontStorePaginationInStorage,
  } = props;

  return (
    <ButtonToolbar className='ml-auto'>
      { children }

      <Dropdown>
        <Dropdown.Toggle variant='outline-secondary' bsPrefix='toolbar' className='border-0'>
          <Icon name='cog' className={ isHighlighted ? 'has-filters' : undefined } />
        </Dropdown.Toggle>

        <Dropdown.Menu>
          <Dropdown.Item as='button' onClick={ () => onSaveColumns() }>
            { dontStorePaginationInStorage ? 'Сохранить настройки столбцов' : 'Сохранить настройки столбцов и пагинации' }
          </Dropdown.Item>
          <Dropdown.Item as='button' onClick={ () => onSaveFilters() }>
            Сохранить настройки фильтра
          </Dropdown.Item>
          <Dropdown.Item as='button' onClick={ () => onSaveAll() }>
            Сохранить настройки столбцов и фильтра
          </Dropdown.Item>
          <Dropdown.Item as='button' onClick={ () => onReset() }>
            Сбросить все настройки
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </ButtonToolbar>
  );
};

/**
 * Конструктор страницы-таблицы: localStorage-настройки (фильтры/скрытые колонки/
 * пагинация), внутренний трекинг загрузки по промисам onDataRequest, быстрый поиск,
 * инжектируемый набор компонентов через children-рендер-проп.
 */
export interface DataTablePageProps {
  loading?: boolean;
  header?: ReactNode;
  /** @see DataTable.propTypes.columns */
  columns?: any[];
  data?: any[];
  dataLength?: number;
  error?: { message?: string } | string | null;
  initialRequest?: {
    offset?: number;
    size?: number;
    sortField?: string;
    sortOrder?: string;
    [key: string]: any;
  };
  pageSize?: number;
  defaultFilterValues?: Record<string, any>;
  defaultHiddenColumnNames?: string[];
  settingsStorageKey?: string;
  /** Не менять title страницы. */
  noDocumentTitle?: boolean;
  children?: any;
  onDataRequest?: (request?: any, anyway?: boolean) => any;
  onHideColumn?: (hiddenColumnNames: string[]) => void;
  statusRefValues?: any[];
  openFilter?: boolean;
  showTableFilterRow?: boolean;
  showTableMultiFilterRow?: boolean;
  dontMakeRequestWhenSort?: boolean;
  onChangeFilters?: (filters: any) => void;
  /** Не сохранять даты в сторадже (при изменении фильтров). */
  dontStoreDatesInStorage?: boolean;
  /** Не сохранять дату «до» в сторадже (по УУ). */
  dontStoreTillDateInStorage?: boolean;
  /** Задержка для обновления данных (блокировка «обновить»). */
  throttlingRefreshTime?: number;
  /** Не сохранять пагинацию (количество страниц) в сторадже. */
  dontStorePaginationInStorage?: boolean;
  localPaging?: boolean;
  /** Функция включения строки в сумму (например, исключаем суммы по вложенным объектам). */
  shouldIncludeRowInSum?: (row: any) => boolean;
  /** Рисовать столбцы по указанной ширине. */
  useColumnWidth?: boolean;
}

const DataTablePage = (props: DataTablePageProps) => {
  const mounted = useUnmountPromise();

  const {
    loading,
    header,
    columns,
    data = EMPTY_DATA,
    dataLength,
    pageSize,
    error,
    initialRequest = DEFAULT_INITIAL_REQUEST,
    noDocumentTitle = false, // не менять title страницы
    defaultHiddenColumnNames = EMPTY_HIDDEN_COLUMN_NAMES,
    defaultFilterValues = EMPTY_FILTER_VALUES,
    settingsStorageKey,
    children,
    onDataRequest,
    onHideColumn,
    statusRefValues = EMPTY_STATUS_REF_VALUES,
    openFilter = false,
    showTableFilterRow = false,
    showTableMultiFilterRow = false,
    dontMakeRequestWhenSort = false,
    onChangeFilters,
    dontStoreDatesInStorage = false,
    dontStoreTillDateInStorage,
    throttlingRefreshTime = 0,
    dontStorePaginationInStorage = false,
    localPaging,
    shouldIncludeRowInSum,
    useColumnWidth,
  } = props;

  const [isDisabled, setIsDisabled] = useState(false);
  const prevTableStateRef = useRef<any>(null);

  // Внутренний трекинг загрузки: DataTablePage сам отслеживает промисы onDataRequest,
  // чтобы скелетон работал без явного прокидывания loading={loading} в каждую страницу.
  // Внешний loading-проп остаётся fallback-ом (effectiveLoading = internalLoading || loading).
  const [internalLoading, setInternalLoading] = useState(false);
  const pendingCountRef = useRef(0);
  // loading || internalLoading — единое значение для всех внутренних использований.
  const effectiveLoading = internalLoading || loading;

  const fixedColumns = columns ? columns?.filter(v => v.fixed).map(v => v.name) : [];

  useEffect(() => {
    let timer;
    if (isDisabled) {
      timer = setTimeout(() => setIsDisabled(false), throttlingRefreshTime);
    }

    return () => clearTimeout(timer); // Очистка при размонтировании
  }, [isDisabled]);

  const settingsKey = `DataTablePage-${ header }-${ settingsStorageKey || 'settings' }`;

  const getSettings = (key?: string, defaults?: any) => {
    try {
      const raw = storage.getItem(settingsKey);
      const settings = raw ? JSON.parse(raw) : {};
      return key ? get(settings, key, defaults) : settings;
    } catch {
      // console.warn(`Failed to parse settings for ${ settingsKey }:`, e);
      // При ошибке парсинга возвращаем дефолтное значение, чтобы не сломать UI
      return key ? defaults : {};
    }
  };

  const hasFilters = !isEmpty(getSettings(filterStorageKey));
  const hasHiddenColumns = !isEmpty(getSettings(hiddenColumnsStorageKey));
  const paginationSizeValue = getSettings(paginationSizeStorageKey);
  const hasPaginationSize = typeof paginationSizeValue === 'number' || !isEmpty(paginationSizeValue);
  const [
    isSettingsStored,
    setSettingsStored,
  ] = useState(hasFilters || hasHiddenColumns || hasPaginationSize);

  // const updateSettings = (settings) => {
  //   const newSettings = {
  //     ...getSettings(),
  //     ...omitBy(settings, (value) => {
  //       // Числа никогда не считаем "пустыми"
  //       if (typeof value === 'number') return false;
  //       return isEmpty(value) && (value !== undefined);
  //     }),
  //   };
  //   storage.setItem(settingsKey, JSON.stringify(newSettings));

  //   // ✅ Пересчитываем на основе НОВЫХ данных
  //   const newHasFilters = !isEmpty(get(newSettings, filterStorageKey));
  //   const newHasHiddenColumns = !isEmpty(get(newSettings, hiddenColumnsStorageKey));
  //   const newPaginationSizeValue = get(newSettings, paginationSizeStorageKey);
  //   const newHasPaginationSize = typeof newPaginationSizeValue === 'number' || !isEmpty(newPaginationSizeValue);

  //   setSettingsStored(newHasFilters || newHasHiddenColumns || newHasPaginationSize);
  // };

  const updateSettings = (settings: Record<string, any>) => {
    try {
      const newSettings = {
        ...getSettings(),
        ...omitBy(settings, (value) => {
          // Числа никогда не считаем "пустыми"
          if (typeof value === 'number') return false;
          return isEmpty(value) && (value !== undefined);
        }),
      };
      storage.setItem(settingsKey, JSON.stringify(newSettings));

      // ✅ Пересчитываем на основе НОВЫХ данных
      const newHasFilters = !isEmpty(get(newSettings, filterStorageKey));
      const newHasHiddenColumns = !isEmpty(get(newSettings, hiddenColumnsStorageKey));
      const newPaginationSizeValue = get(newSettings, paginationSizeStorageKey);
      const newHasPaginationSize = typeof newPaginationSizeValue === 'number' || !isEmpty(newPaginationSizeValue);

      setSettingsStored(newHasFilters || newHasHiddenColumns || newHasPaginationSize);

    } catch {
      // console.warn('Failed to update settings in localStorage:', e);
      // Опционально: можно добавить флаг, чтобы показать пользователю, что настройки не сохраняются
    }
  };

  const storedTableState = getSettings(stateStorageKey, {});
  const storedFilters = getSettings(filterStorageKey, defaultFilterValues);

  const storedHiddenColumnNames = getSettings(hiddenColumnsStorageKey, defaultHiddenColumnNames);
  const storedPaginationSize = getSettings(paginationSizeStorageKey, undefined);

  // Сохранённые ширины колонок (ресайз/автоширина DataTableV2). Читаем с
  // санитайзом — битый localStorage не должен ломать таблицу. ВАЖНО: в флаг
  // isSettingsStored ширины НЕ входят — тот управляет сидом visible:false-
  // колонок, и один ресайз не должен раскрывать их после F5.
  const storedColumnWidthsRaw = getSettings(columnWidthsStorageKey);
  const storedColumnWidths = storedColumnWidthsRaw
    && typeof storedColumnWidthsRaw === 'object'
    && !Array.isArray(storedColumnWidthsRaw)
    ? Object.fromEntries(Object.entries(storedColumnWidthsRaw as Record<string, unknown>)
      .filter(([, width]) => typeof width === 'number' && Number.isFinite(width) && width > 0))
    : undefined;
  const initialSize =
    storedTableState.size ??
    (typeof storedPaginationSize === 'number' ? storedPaginationSize : undefined) ??
    pageSize ??
    initialRequest.size;

  const preparedFilters1 = dontStoreDatesInStorage ?
    omit(storedFilters, ['sinceCreatedDate', 'tillCreatedDate', 'isRepeat'])
    : storedFilters

  // По УУ бизнес попросил не хранить даты "до":
  const preparedFilters2 = dontStoreTillDateInStorage ?
    omit(preparedFilters1, ['tillCreatedDate'])
    : preparedFilters1

  const [filters, setFilters] = useState(preparedFilters2);
  const [isFilterOpen, toggleFilter] = useState(openFilter);
  const [quickSearch, setQuickSearch] = useState<string | null>(null);

  // используем useRef, чтобы предотвратить ненужный rerender
  const hiddenColumnNames = useRef(null);
  const setHiddenColumnNames = (value: any) => { hiddenColumnNames.current = value; };

  const [tableState, setTableState] = useReducer((currentState: any, newState: any) => {
    const state = {
      ...currentState,
      ...newState,
      // TODO fix -10 offset
      ...((newState.offset < 0) ? { offset: 0 } : {}),
    };

    updateSettings({
      [stateStorageKey]: state,
    });

    return state;
  }, { ...initialRequest, size: initialSize, ...storedTableState });

  const handleRequestData = (anyway = false) => {
    if (typeof onDataRequest === 'function') {
      const quickSearchText = quickSearch?.length ? quickSearch : null;

      const sRV = (statusRefValues && (statusRefValues.length === 1) && statusRefValues[0] === 29)
        ? [...statusRefValues]
        : [...(filters?.statusRefValues ?? [])];

      const request = {
        ...filters,
        ...tableState,
        statusRefValues: sRV,
      };

      return onDataRequest(quickSearchText
        ? { ...request, quickSearch: quickSearchText }
        : request, anyway);
    }

    return Promise.resolve(data);
  };

  // const wrappedHandleRequestData = (anyway = false) => {
  //   if (typeof handleRequestData === 'function' && handleRequestData !== null && 'then' in handleRequestData) {
  //     mounted(handleRequestData(anyway));
  //   } else { handleRequestData(anyway); }
  // };

  // Трекает промис onDataRequest для внутреннего индикатора загрузки (скелетон/спиннер).
  // Не-thenable результат (например, no-op ветка loadData) пропускается — загрузка не зависнет.
  const trackRequest = (result: any) => {
    if (!result || typeof result.then !== 'function') return;
    pendingCountRef.current += 1;
    setInternalLoading(true);
    // mounted() (useUnmountPromise) не вызовет onDone после размонтирования компонента.
    const onDone = () => {
      pendingCountRef.current = Math.max(0, pendingCountRef.current - 1);
      if (pendingCountRef.current === 0) setInternalLoading(false);
    };
    mounted(result).then(onDone, onDone);
  };

  const wrappedHandleRequestData = (anyway = false) => {
    // Сначала вызываем и получаем результат
    const result = handleRequestData(anyway);
    // Проверяем, является ли результат Promise-подобным объектом, и трекаем загрузку.
    if (result && typeof result.then === 'function') {
      mounted(result);
    }
    trackRequest(result);
  };

  useEffect(() => {
    if (!isEmpty(filters)) {
      setFilters(filters);
    }
    wrappedHandleRequestData(true);
    const handleUnload = () => {
      updateSettings({ [stateStorageKey]: undefined });
    };

    // сбросим настройки текущей выбранной страницы в таблице при обновлении страницы браузера,
    // так как эти настройки хранятся в localstorage, чтоб не дублировать логику
    window.addEventListener('unload', handleUnload);

    return () => window.removeEventListener('unload', handleUnload);
  }, [])

  useComponentDidUpdate(
    () => {
      if (!effectiveLoading) {
        setTableState({ offset: 0 });
        wrappedHandleRequestData();
        // mounted(handleRequestData());
      }
    },
    [statusRefValues]
  );

  // 1. Инициализируем ref при сборке
  useEffect(() => {
    prevTableStateRef.current = { ...tableState };
  }, []);

  useComponentDidUpdate(() => {
    const prev = prevTableStateRef.current;
    const curr = tableState;

    // 🎯 ПЕРВЫЙ ЗАПУСК: просто сохраняем состояние и выходим
    if (!prev) {
      prevTableStateRef.current = { ...curr };
      return;
    }

    // Определяем, что именно изменилось
    const sortChanged = (
      prev.sortField !== curr.sortField ||
    prev.sortOrder !== curr.sortOrder
    );
    const offsetChanged = prev.offset !== curr.offset;
    const sizeChanged = prev.size !== curr.size;
    const paginationChanged = offsetChanged || sizeChanged;

    // Обновляем ref для следующего рендера
    prevTableStateRef.current = { ...curr };

    // 🚫 БЛОКИРОВКА ЗАПРОСОВ:

    // 1. Сортировка + блокировка включена → не делаем запрос (локальная сортировка)
    if (sortChanged && dontMakeRequestWhenSort) {
      return;
    }

    // 2. Пагинация + локальная пагинация включена → не делаем запрос
    if (paginationChanged && localPaging) {
      return;
    }

    // ✅ В остальных случаях делаем запрос:
    // - изменились фильтры (через quickSearch)
    // - серверная пагинация (offset/size)
    // - dontMakeRequestWhenSort/localPaging выключены
    wrappedHandleRequestData();

  }, [quickSearch, tableState, dontMakeRequestWhenSort, localPaging]);

  useComponentDidUpdate(() => {
    setTableState({ offset: 0 });
  }, [filters]);

  useEffect(() => {
    if (onChangeFilters) {
      onChangeFilters(filters);
    }
  }, [filters]);

  const handleToggleFilter = () => {
    toggleFilter(!isFilterOpen);
    setQuickSearch(null);
  };

  const handleSubmitFilter = (filter: any) => {
    const values = filter === false ? {} : filter;
    const newFilters = omitBy(values, isNil);

    setFilters(newFilters);
  };

  const handleSubmitQuickSearch = (value: any) => {
    setQuickSearch(String(value || '').trim());
    setFilters({});
  };

  const handleSortingChange = (columnName: string, direction: string) => {
    setTableState({ sortField: columnName, sortOrder: direction });
  };

  const handleCurrentPageChange = (currentPage: number) => {
    setTableState({ offset: currentPage });
  };

  const handlePageSizeChange = (pageRows: number) => {
    setTableState({ size: pageRows });
  };

  const handleHideColumn = (cols: string[]) => {
    setHiddenColumnNames(cols);

    if (typeof onHideColumn === 'function') {
      onHideColumn(cols);
    }
  };

  // Коммит ресайза/автоширины колонок DataTableV2 — сохраняем в настройки
  // страницы (пустая карта = «Сбросить вид» — убираем ключ из хранилища)
  const handleColumnWidthsChange = (widths: Record<string, number>) => {
    updateSettings({
      [columnWidthsStorageKey]: isEmpty(widths) ? undefined : widths,
    });
  };

  const handleSaveColumnsSettings = () => {
    updateSettings({
      [hiddenColumnsStorageKey]: hiddenColumnNames.current,
      [paginationSizeStorageKey]: dontStorePaginationInStorage ? undefined : tableState.size,
    });
  };

  const handleSaveFiltersSettings = () => {
    updateSettings({
      [filterStorageKey]: filters,
    });
  };

  const handleSaveAllSettings = () => {
    updateSettings({
      [filterStorageKey]: filters,
      [hiddenColumnsStorageKey]: hiddenColumnNames.current,
      [paginationSizeStorageKey]: dontStorePaginationInStorage ? undefined : tableState.size,
    });
  };

  const handleResetSettings = () => {
    updateSettings({
      [filterStorageKey]: undefined,
      [hiddenColumnsStorageKey]: undefined,
      [paginationSizeStorageKey]: undefined,
      [columnWidthsStorageKey]: undefined,
    });

    setHiddenColumnNames(defaultHiddenColumnNames);
    setFilters(defaultFilterValues);
  };

  const PageHeader = () => (
    <>
      { !noDocumentTitle ? <DocumentTitle title={ header } /> : null }

      <div className='datatable-page-title'>
        <h4 style={ { display: 'inline' } }>
          { header }

          { onDataRequest && (
            <Button
              variant='link'
              disabled={ effectiveLoading || isDisabled }
              onClick={ () => {
                if (throttlingRefreshTime > 0) {
                  setIsDisabled(true);
                };
                wrappedHandleRequestData(true);
              }
              }
            >
              <Icon name='refresh' spin={ effectiveLoading } />
            </Button>
          ) }
        </h4>
      </div>
    </>
  );

  const QuickSearch = (quickSearchProps: any) => (
    <QuickSearchInput
      name='searchText'
      value={ quickSearch }
      placeholder='Быстрый поиск'
      disabled={ effectiveLoading }
      onClear={ handleSubmitQuickSearch }
      onSubmit={ handleSubmitQuickSearch }
      { ...quickSearchProps }
    />
  );

  const ToggleFilterButton = (buttonProps: any) => (
    <Button
      text='Фильтровать'
      icon='filter'
      variant='outline-primary'
      disabled={ effectiveLoading }
      className={ cx({ 'active': isFilterOpen }) }
      onClick={ handleToggleFilter }
      { ...buttonProps }
    />
  );

  const renderToolbar = (toolbarProps: any) => (
    <DataTableToolbar
      { ...toolbarProps }
      isHighlighted={ isSettingsStored }
      onSaveColumns={ handleSaveColumnsSettings }
      onSaveFilters={ handleSaveFiltersSettings }
      onSaveAll={ handleSaveAllSettings }
      onReset={ handleResetSettings }
      dontStorePaginationInStorage={ dontStorePaginationInStorage }
    />
  );

  const CustomDataTable = (dataTableProps: any) => (
    <CustomDataTableComponent
      loading={ effectiveLoading }
      data={ data }
      dataLength={ dataLength }
      columns={ columns }
      defaultHiddenColumnNames={ storedHiddenColumnNames }
      isSettingsStored={ isSettingsStored }
      currentPage={ tableState.offset / tableState.size }
      pageSize={ tableState.size }
      sortField={ tableState.sortField }
      sortOrder={ tableState.sortOrder }
      error={ error }
      errorRetryHandler={ handleRequestData }
      toolbarComponent={ renderToolbar }
      onSortingChange={ handleSortingChange }
      onCurrentPageChange={ handleCurrentPageChange }
      onPageSizeChange={ handlePageSizeChange }
      onHideColumn={ handleHideColumn }
      showTableFilterRow={ showTableFilterRow }
      showTableMultiFilterRow={ showTableMultiFilterRow }
      shouldIncludeRowInSum={ shouldIncludeRowInSum ?? null }
      useColumnWidth={ useColumnWidth }
      { ...dataTableProps }
    />
  );

  const DataTable = (dataTableProps: any) => {
    // Фаза 3 (замена DevExpress-грида): страницы с fixed-колонками раньше шли в
    // CustomDataTable, остальные — в dx DataTable; обе ветки заменяет DataTableV2.
    // Откат на старые движки — localStorage 'DataTable-Engine' = 'legacy'.
    if (isDataTableV2Enabled()) {
      const customDialect = fixedColumns.length > 0;

      // CustomDataTable не фильтровал по searchValue (поиск выполняет сервер) —
      // на fixed-страницах срезаем его, чтобы V2 не начала фильтровать повторно
      const passthroughProps = { ...dataTableProps };

      if (customDialect) {
        delete passthroughProps.searchValue;
      }

      return (
        <DataTableV2
          loading={ effectiveLoading }
          data={ data }
          dataLength={ dataLength }
          columns={ columns }
          defaultHiddenColumnNames={ storedHiddenColumnNames }
          isSettingsStored={ isSettingsStored }
          currentPage={ tableState.offset / tableState.size }
          pageSize={ tableState.size }
          sortField={ tableState.sortField }
          sortOrder={ tableState.sortOrder }
          error={ error }
          errorRetryHandler={ handleRequestData }
          toolbarComponent={ renderToolbar }
          onSortingChange={ handleSortingChange }
          onCurrentPageChange={ handleCurrentPageChange }
          onPageSizeChange={ handlePageSizeChange }
          onHideColumn={ handleHideColumn }
          // персистентность ширин колонок (ресайз/автоширина)
          defaultColumnWidths={ storedColumnWidths }
          onColumnWidthsChange={ handleColumnWidthsChange }
          showTableFilterRow={ showTableFilterRow }
          showTableMultiFilterRow={ showTableMultiFilterRow }
          // диалект CustomDataTable (fixed-страницы): локальные сортировка/пагинация
          // по умолчанию, кап высоты 700, выравнивание textAlign||'right',
          // сортировка по сырому row[name] (getCellValue там возвращает JSX),
          // «коробочная» тема CustomDataTable (остальные — в dx-теме, как раньше)
          { ...(customDialect ? {
            localSorting: true,
            localPaging: true,
            maxBodyHeight: 700,
            defaultAlign: 'right',
            rawSortValues: true,
            shouldIncludeRowInSum,
            useColumnWidth,
            variant: 'custom' as const,
          } : {}) }
          { ...passthroughProps }
        />
      );
    }

    if (fixedColumns && fixedColumns.length > 0) {
      return CustomDataTable(dataTableProps)
    }
    else {
      return (<DataTableComponent
        loading={ effectiveLoading }
        data={ data }
        dataLength={ dataLength }
        columns={ columns }
        defaultHiddenColumnNames={ storedHiddenColumnNames }
        isSettingsStored={ isSettingsStored }
        currentPage={ tableState.offset / tableState.size }
        pageSize={ tableState.size }
        sortField={ tableState.sortField }
        sortOrder={ tableState.sortOrder }
        error={ error }
        errorRetryHandler={ handleRequestData }
        toolbarComponent={ renderToolbar }
        onSortingChange={ handleSortingChange }
        onCurrentPageChange={ handleCurrentPageChange }
        onPageSizeChange={ handlePageSizeChange }
        onHideColumn={ handleHideColumn }
        showTableFilterRow={ showTableFilterRow }
        showTableMultiFilterRow={ showTableMultiFilterRow }
        { ...dataTableProps }
      />);
    };
  }

  return (typeof children === 'function') ? children({
    filters,
    quickSearch,
    tableState,
    isFilterOpen,
    handleRequestData,
    handleToggleFilter,
    handleSubmitFilter,
    handleSubmitQuickSearch,
    components: {
      PageHeader,
      QuickSearch,
      ToggleFilterButton,
      DataTable,
    },
  }) : children;
};

export default memo(DataTablePage);
